// src/pages/Login.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { authenticatedFetch, generateToken, storeCredentials } from '../utils/api';

// ✅ FIXED: No trailing spaces in fallback URL (critical!)
const API_BASE = (process.env.REACT_APP_API_URL);

function Login() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [isResendingOtp, setIsResendingOtp] = useState(false);

  // ✅ Load remembered email on mount
  useEffect(() => {
    const savedEmail = localStorage.getItem('rememberedEmail');
    if (savedEmail) {
      setFormData(prev => ({ ...prev, email: savedEmail }));
      setRememberMe(true);
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.email) newErrors.email = 'Email address is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Please enter a valid email';
    if (!formData.password) newErrors.password = 'Password is required';
    else if (formData.password.length < 6) newErrors.password = 'Password must be at least 6 characters';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ✅✅✅ UPDATED: Resend OTP - calls /resend-otp + redirects to /verify-account ✅✅✅
  const handleResendOtp = async (e) => {
    e?.preventDefault();
    
    if (!formData.email) {
      setErrors({ submit: 'Please enter your email first' });
      toast.warning('Please enter your email first');
      return;
    }
    
    setIsResendingOtp(true);
    setErrors({});
    
    try {
      console.log('📤 Resending OTP for:', formData.email);
      
      const payload = { Email: formData.email.trim().toLowerCase() };
      
      const result = await authenticatedFetch(
        `${API_BASE}/HMO/resend-otp`,
        'POST',
        payload
      );
      
      console.log('✅ Resend Response:', result);
      
      if (result?.success === true || result?.status === 'success') {
        const successMsg = result.message || 'Verification code resent! Check your email.';
        setErrors({ submit: successMsg });
        toast.success(successMsg);
        
        setTimeout(() => {
          navigate('/verify-account', {
            replace: true,
            state: {
              email: formData.email.trim().toLowerCase(),
              fromLogin: true,
              message: 'A new verification code has been sent to your email.'
            }
          });
        }, 1500);
        
      } else {
        const errorMsg = result?.message || 'Failed to resend code. Please try again.';
        setErrors({ submit: errorMsg });
        toast.error(errorMsg);
      }
    } catch (error) {
      console.error('💥 Resend OTP Error:', { message: error.message, name: error.name });
      const errorResponse = error.response?.data || error.data || {};
      const errorMsg = errorResponse?.message || error.message || 'Cannot connect to server. Please try again.';
      setErrors({ submit: errorMsg });
      toast.error(errorMsg);
    } finally {
      setIsResendingOtp(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    setIsLoading(true);
    setErrors({});
    
    try {
      const payload = {
        Email: formData.email.trim().toLowerCase(),
        Password: formData.password
      };

      console.log('📤 Login Request:', { ...payload, Password: '***' });
      console.log('🔍 API URL:', `${API_BASE}/HMO/login`);

      const response = await authenticatedFetch(
        `${API_BASE}/HMO/login`,
        'POST',
        payload
      );

      console.log('✅ Login Response:', response);

      // ✅ Handle success
      if (response?.success === true) {
        // ✅ Store session data - sessionId is CompanyId (integer)
        const sessionData = {
          sessionId: response.sessionId,
          companyId: response.companyId || response.sessionId,
          userEmail: formData.email.toLowerCase().trim(),
          firstName: response.data?.firstName || response.data?.FirstName || '',
          lastName: response.data?.lastName || response.data?.LastName || '',
          businessName: response.data?.businessName || response.data?.BusinessName || '',
          isOnboarded: response.isOnboarded,
          isCompleteRegistration: response.isCompleteRegistration,
          isSchedule: response.isSchedule,
          expiresAt: response.expiresInSeconds 
            ? Date.now() + (response.expiresInSeconds * 1000) 
            : Date.now() + (120 * 1000),
          message: response.message,
          loggedInAt: new Date().toISOString()
        };
        
        // ✅ Save to localStorage
        localStorage.setItem('userSession', JSON.stringify(sessionData));
        console.log('💾 Stored session:', sessionData);
        
        // ✅ Remember email if checked
        if (rememberMe) {
          localStorage.setItem('rememberedEmail', formData.email);
        } else {
          localStorage.removeItem('rememberedEmail');
        }
        
        // ✅ Pre-generate API credentials for subsequent requests
        try {
          const credentials = await generateToken();
          storeCredentials(credentials);
          console.log('✅ API credentials stored');
        } catch (tokenErr) {
          console.warn('⚠️ Token generation warning:', tokenErr);
        }
        
        // ✅ Show success
        toast.success(response.message || 'Welcome back!');
        
        // ✅✅✅ SMART REDIRECT LOGIC - Check IsSchedule === 0 ✅✅✅
        setTimeout(() => {
          const isOnboarded = response.isOnboarded === true;
          const isCompleteRegistration = response.isCompleteRegistration === true;
          const isSchedule = response.isSchedule === true;
          
          console.log('🔍 Redirect check:', { isOnboarded, isCompleteRegistration, isSchedule });
          
          let targetRoute = '/onboarding-dashboard';
          
          // ✅ Priority 1: Fully onboarded → Main dashboard
          if (isOnboarded) {
            targetRoute = '/dashboard';
            console.log('🧭 User fully onboarded → /dashboard');
          }
          // ✅ Priority 2: NOT scheduled yet (IsSchedule = 0) → Schedule activation
          else if (!isSchedule && isCompleteRegistration) {
            targetRoute = '/schedule-activation';
            console.log('🧭 Registration complete, not scheduled → /schedule-activation');
          }
          // ✅ Priority 3: Registration incomplete → Business info
          else if (!isCompleteRegistration) {
            targetRoute = '/business-info';
            console.log('🧭 Registration incomplete → /business-info');
          }
          // ✅ Priority 4: Default fallback
          else {
            targetRoute = '/onboarding-dashboard';
            console.log('🧭 Default → /onboarding-dashboard');
          }
          
          navigate(targetRoute, { 
            replace: true,
            state: { 
              fromLogin: true, 
              sessionId: response.sessionId,
              needsOnboarding: !isOnboarded,
              isCompleteRegistration: isCompleteRegistration,
              isSchedule: isSchedule
            }
          });
        }, 500);
        
      } else {
        // ✅ Handle business logic errors
        const errorMsg = response?.message || 'Login failed. Please try again.';
        
        // ✅ Check for email not verified
        if (errorMsg.toLowerCase().includes('email not verified') || 
            errorMsg.toLowerCase().includes('not verified')) {
          setErrors({ submit: 'Please verify your email first. Check your inbox for the verification code.' });
          toast.warning('Email not verified');
          return;
        }
        
        // ✅ Handle other errors
        if (errorMsg.toLowerCase().includes('invalid')) {
          setErrors({ submit: 'Invalid email or password. Please try again.' });
          toast.error('Invalid credentials');
        } else {
          setErrors({ submit: errorMsg });
          toast.error(errorMsg);
        }
      }
      
    } catch (error) {
      console.error('💥 Login Error:', { 
        message: error.message, 
        name: error.name,
        stack: error.stack,
        response: error.response
      });
      
      // ✅ Parse error response if available
      const errorResponse = error.response?.data || error.data || {};
      const errorMsg = errorResponse?.message || error.message || 'An error occurred. Please try again.';
      
      // ✅ Check for email not verified in caught error (401 responses)
      if (errorMsg.toLowerCase().includes('email not verified') || 
          errorMsg.toLowerCase().includes('not verified')) {
        setErrors({ submit: 'Please verify your email first. Check your inbox for the verification code.' });
        toast.warning('Email not verified');
        return;
      }
      
      // ✅ Handle other specific errors
      let msg = 'An error occurred. Please try again.';
      
      if (error.message?.includes('Missing headers') || 
          error.message?.includes('AuthorizationKey') ||
          error.message?.includes('Token') ||
          error.message?.includes('SecretKey')) {
        msg = 'Authentication configuration error. Please contact support.';
      } else if (error.message?.includes('Failed to fetch') || error.name === 'TypeError') {
        msg = 'Cannot connect to server. Check your internet connection.';
      } else if (error.message?.includes('401') || error.message?.includes('Unauthorized')) {
        if (!errorMsg.toLowerCase().includes('invalid') && !errorMsg.toLowerCase().includes('verified')) {
          msg = 'Authentication failed. Please check your credentials.';
        } else {
          msg = errorMsg;
        }
      } else if (error.message?.includes('404') || error.message?.includes('Not Found')) {
        msg = 'API endpoint not found. Please contact support.';
      } else {
        msg = errorMsg;
      }
      
      setErrors({ submit: msg });
      toast.error(msg);
      
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => setShowPassword(!showPassword);

  const handleForgotPassword = (e) => {
    e?.preventDefault();
    navigate('/forgot-password', { 
      state: { 
        prefillEmail: formData.email || ''
      } 
    });
  };

  return (
    <div className="login-page">
      <ToastContainer position="top-right" autoClose={4000} hideProgressBar={false} newestOnTop closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover theme="light" />

      {/* ✅✅✅ NEW: Top Banner for "Sending OTP..." during resend ✅✅✅ */}
      {isResendingOtp && (
        <div className="otp-sending-banner" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          backgroundColor: '#F89423',
          color: 'white',
          padding: '12px 20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '10px',
          zIndex: 9999,
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          animation: 'slideDown 0.3s ease'
        }}>
          <span 
            className="spinner-border spinner-border-sm" 
            role="status" 
            aria-hidden="true"
            style={{ 
              width: '18px', 
              height: '18px', 
              borderWidth: '2px',
              animation: 'spin 1s linear infinite',
              borderColor: 'white transparent white transparent'
            }}
          ></span>
          <span style={{ fontWeight: '500', fontSize: '14px' }}>
            Sending verification code to {formData.email}...
          </span>
        </div>
      )}

      {/* Header */}
      <header className="header-area header-one" style={{ marginTop: isResendingOtp ? '44px' : '0' }}>
        <div className="container">
          <div className="d-flex justify-content-between align-items-center py-3">
            <Link to="/" className="brand-logo" aria-label="FSL HMO Home">
              <img src="images/fsl_logo.png" alt="FSL HMO Logo" style={{ height: '50px' }} />
            </Link>
            <button className="theme-btn style-two" onClick={() => navigate('/')} disabled={isLoading} aria-label="Back to Home">← Back to Home</button>
          </div>
        </div>
      </header>

      {/* Login Form */}
      <section className="login-section py-5">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-5 col-md-7">
              <div className="login-container">
                <div className="login-header text-center mb-4">
                  <h1 className="login-title mb-2">Welcome Back</h1>
                  <p className="login-subtitle">Login to your FSL HMO account to continue</p>
                </div>

                <form onSubmit={handleSubmit} className="login-form" noValidate>
                  {/* ✅ Your existing alert pattern - shows errors.submit */}
                  {errors.submit && (
                    <div className={`alert mb-3 border-0 ${errors.submit.toLowerCase().includes('verify') ? 'alert-warning' : 'alert-danger'}`} role="alert" aria-live="polite">
                      <i className={`fas me-2 ${errors.submit.toLowerCase().includes('verify') ? 'fa-envelope-open-text' : 'fa-exclamation-circle'}`} aria-hidden="true"></i>
                      {errors.submit}
                      {/* ✅ Show Resend button only for email not verified */}
                      {errors.submit.toLowerCase().includes('verify') && (
                        <div className="mt-2">
                          <button
                            type="button"
                            className="btn btn-sm"
                            onClick={handleResendOtp}
                            disabled={isResendingOtp}
                            style={{ 
                              backgroundColor: isResendingOtp ? '#F89423' : '#6c757d',
                              color: 'white',
                              border: 'none',
                              fontWeight: '500',
                              padding: '6px 16px',
                              borderRadius: '6px',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px',
                              cursor: isResendingOtp ? 'not-allowed' : 'pointer',
                              opacity: isResendingOtp ? 0.85 : 1,
                              transition: 'all 0.2s ease'
                            }}
                            title={isResendingOtp ? 'Sending verification code...' : 'Resend verification code'}
                          >
                            {isResendingOtp ? (
                              <>
                                {/* ✅ Spinner shows IMMEDIATELY on click */}
                                <span 
                                  className="spinner-border spinner-border-sm" 
                                  role="status" 
                                  aria-hidden="true"
                                  style={{ 
                                    width: '16px', 
                                    height: '16px', 
                                    borderWidth: '2px',
                                    animation: 'spin 1s linear infinite'
                                  }}
                                ></span>
                                <span>Sending...</span>
                              </>
                            ) : (
                              <>
                                <i className="fas fa-redo" style={{ fontSize: '14px' }}></i>
                                <span>Resend Code</span>
                              </>
                            )}
                          </button>
                          <small className="text-muted d-block mt-1">
                            <i className="fas fa-info-circle me-1"></i>
                            Check spam folder if you don't see the email
                          </small>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Email */}
                  <div className="form-group mb-3">
                    <label htmlFor="email" className="form-label">Email address <span className="text-danger">*</span></label>
                    <input type="email" id="email" name="email" className={`form-control ${errors.email ? 'is-invalid' : ''}`} placeholder="Enter your email" value={formData.email} onChange={handleChange} disabled={isLoading || isResendingOtp} autoComplete="email" required aria-invalid={!!errors.email} />
                    {errors.email && <div className="invalid-feedback" role="alert">{errors.email}</div>}
                  </div>

                  {/* Password */}
                  <div className="form-group mb-3">
                    <label htmlFor="password" className="form-label">Password <span className="text-danger">*</span></label>
                    <div className="password-input-wrapper">
                      <input type={showPassword ? 'text' : 'password'} id="password" name="password" className={`form-control ${errors.password ? 'is-invalid' : ''}`} placeholder="Enter your password" value={formData.password} onChange={handleChange} disabled={isLoading || isResendingOtp} autoComplete="current-password" required aria-invalid={!!errors.password} />
                      <button type="button" className="password-toggle-btn" onClick={togglePasswordVisibility} disabled={isLoading || isResendingOtp} aria-label={showPassword ? 'Hide password' : 'Show password'}>
                        {showPassword ? <i className="fas fa-eye-slash" aria-hidden="true"></i> : <i className="fas fa-eye" aria-hidden="true"></i>}
                      </button>
                    </div>
                    {errors.password && <div className="invalid-feedback" role="alert">{errors.password}</div>}
                    
                    {/* Forgot Password Link */}
                    <div className="text-end mt-2">
                      <Link to="/forgot-password" className="forgot-password-link">
                        Forgot Password?
                      </Link>
                    </div>
                  </div>

                  {/* Remember Me */}
                  <div className="form-check mb-3">
                    <input type="checkbox" className="form-check-input" id="rememberMe" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} disabled={isLoading || isResendingOtp} />
                    <label className="form-check-label" htmlFor="rememberMe">Remember me on this device</label>
                  </div>

                  {/* Submit Button */}
                  <button type="submit" className="btn btn-login w-100 mt-4 mb-3" disabled={isLoading || isResendingOtp} aria-busy={isLoading}>
                    {isLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Logging in...
                      </>
                    ) : (
                      <>
                        Login <i className="fas fa-arrow-right ms-2" aria-hidden="true"></i>
                      </>
                    )}
                  </button>

                  {/* Sign Up */}
                  <div className="text-center mt-3">
                    <p className="mb-0 text-muted">Don't have an account? <Link to="/get-started" className="signup-link">Get started</Link></p>
                  </div>

                  {/* Security */}
                  <div className="text-center mt-4 pt-3 border-top">
                    <small className="text-muted"><i className="fas fa-shield-alt me-1" aria-hidden="true"></i>Your connection is secure and encrypted</small>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CSS */}
      <style>{`
        .login-page { min-height: 100vh; background-color: #fafafa; display: flex; flex-direction: column; font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }
        .login-section { flex: 1; display: flex; align-items: center; }
        .login-container { background: white; border-radius: 16px; box-shadow: 0 12px 40px rgba(0,0,0,0.08); padding: 40px; width: 100%; animation: slideUp 0.5s ease; border-top: 4px solid #F89423; }
        @keyframes slideUp { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }
        
        /* ✅ NEW: Banner slide-down animation */
        @keyframes slideDown {
          from { transform: translateY(-100%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        
        /* ✅ NEW: Spinner animation */
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .spinner-border {
          border-color: currentColor transparent currentColor transparent !important;
        }
        
        .login-header { margin-bottom: 30px; }
        .login-title { font-size: 28px; font-weight: 700; color: #1f2937; margin-bottom: 8px; }
        .login-subtitle { color: #6b7280; font-size: 14px; margin: 0; }
        .form-label { font-weight: 600; color: #374151; font-size: 14px; margin-bottom: 8px; display: block; }
        .form-control { padding: 12px 16px; border: 1px solid #d1d5db; border-radius: 10px; font-size: 14px; transition: all 0.3s ease; background-color: #f9fafb; width: 100%; }
        .form-control:focus { border-color: #F89423; box-shadow: 0 0 0 4px rgba(248,148,35,0.15); background-color: white; outline: none; }
        .form-control.is-invalid { border-color: #ef4444; background-color: #fef2f2; }
        .form-control.is-invalid:focus { box-shadow: 0 0 0 4px rgba(239,68,68,0.15); }
        .form-control:disabled { background-color: #f3f4f6; cursor: not-allowed; opacity: 0.8; }
        .invalid-feedback { color: #ef4444; font-size: 12px; margin-top: 4px; display: block; }
        .password-input-wrapper { position: relative; }
        .password-toggle-btn { position: absolute; right: 12px; top: 50%; transform: translateY(-50%); background: none; border: none; color: #6b7280; cursor: pointer; padding: 8px; transition: color 0.3s ease; display: flex; align-items: center; justify-content: center; }
        .password-toggle-btn:hover:not(:disabled) { color: #F89423; }
        .password-toggle-btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .forgot-password-link { color: #F89423; text-decoration: none; font-size: 13px; font-weight: 500; transition: color 0.3s ease; background: none; border: none; padding: 0; cursor: pointer; }
        .forgot-password-link:hover:not(:disabled) { color: #ea7c1f; text-decoration: underline; }
        .form-check-input { width: 18px; height: 18px; border-color: #d1d5db; cursor: pointer; }
        .form-check-input:checked { background-color: #F89423; border-color: #F89423; }
        .form-check-label { font-size: 14px; color: #4b5563; margin-left: 8px; cursor: pointer; }
        .btn-login { background: linear-gradient(135deg, #F89423 0%, #ea7c1f 100%); border: none; color: white; padding: 14px 20px; font-size: 15px; font-weight: 600; border-radius: 10px; cursor: pointer; transition: all 0.3s ease; display: flex; align-items: center; justify-content: center; width: 100%; }
        .btn-login:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(248,148,35,0.4); }
        .btn-login:disabled { opacity: 0.75; cursor: not-allowed; transform: none; }
        
        /* ✅ NEW: Resend button hover effect */
        .btn-sm:not(:disabled):hover {
          transform: translateY(-1px);
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }
        
        .signup-link { color: #F89423; text-decoration: none; font-weight: 600; }
        .signup-link:hover { color: #ea7c1f; text-decoration: underline; }
        .alert { padding: 12px 16px; border-radius: 10px; font-size: 14px; display: flex; align-items: flex-start; }
        .alert-danger { background-color: #fef2f2; color: #dc2626; border: 1px solid #fecaca; }
        .alert-warning { background-color: #fff7ed; color: #92400e; border: 1px solid #fed7aa; }
        .alert-danger i, .alert-warning i { margin-top: 2px; }
        .spinner-border { width: 1rem; height: 1rem; border-width: 0.15em; vertical-align: text-bottom; }
        @media (max-width: 768px) { .login-container { padding: 30px 20px; border-radius: 12px; } .login-title { font-size: 24px; } }
        @media (max-width: 480px) { .login-section { padding: 20px 0; } .login-container { padding: 25px 15px; } .btn-login { padding: 12px; font-size: 14px; } }
        .form-control:focus-visible, .btn-login:focus-visible, .password-toggle-btn:focus-visible, .forgot-password-link:focus-visible { outline: 2px solid #F89423; outline-offset: 2px; }
      `}</style>
    </div>
  );
}

export default Login;